/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useLocation } from 'js/lib/useRouter';

import { Divider, Grid } from '@coursera/cds-core';

import { usePageConfigComponentTrackingData } from 'bundles/page-config-common/contexts/PageConfigComponentMetadataContext';
import BrowseInSearchCollections from 'bundles/search-common/components/experiments/browseInSerp/BrowseInSearchCollections';
import BrowseInSearchCollectionsPlaceholder from 'bundles/search-common/components/experiments/browseInSerp/BrowseInSearchCollectionsPlaceholder';
import {
  useBrowseInSerpCollection,
  useQueryMatchSkillTaxonomy2,
} from 'bundles/search-common/components/experiments/browseInSerp/BrowseInSerpUtils';
import PartnerSection, {
  PartnerSectionLoading,
} from 'bundles/search-common/components/experiments/browseInSerp/PartnerSection';
import RelatedTopics, {
  RelatedSkillsLoading,
} from 'bundles/search-common/components/experiments/browseInSerp/RelatedTopics';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchPageEventingData } from 'bundles/search-common/types';
import { getShouldShowBrowseInSERPWithImpression } from 'bundles/search-common/utils/experimentUtils';

const styles = {
  dividerStyles: css`
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-100);
    padding-left: var(--cds-spacing-100);
    padding-right: var(--cds-spacing-100);
  `,
};
type Props = { eventingData?: SearchPageEventingData };

const BrowseInSearch = ({ eventingData }: Props) => {
  const {
    query: { query },
  } = useLocation();
  const { isLoading } = useSearch();
  const isInTestVariant = getShouldShowBrowseInSERPWithImpression();
  const queryToUse = isInTestVariant ? query : null; // null to prevent from making API call
  const skillTaxonomyData = useQueryMatchSkillTaxonomy2(queryToUse);
  const isInSkillTaxonomy2 = skillTaxonomyData !== null;
  const shouldShowBrowseInSerp = isInTestVariant && isInSkillTaxonomy2;
  const skill = shouldShowBrowseInSerp ? skillTaxonomyData : null;
  const hasBrowseInSerpCollection = useBrowseInSerpCollection();

  const pageConfigComponentTrackingData = usePageConfigComponentTrackingData();

  if (isInTestVariant && isLoading) {
    return (
      <Grid item xs={12}>
        <BrowseInSearchCollectionsPlaceholder />
        <PartnerSectionLoading />
        <RelatedSkillsLoading />
        <Divider css={styles.dividerStyles} />
      </Grid>
    );
  }

  if (skill && hasBrowseInSerpCollection) {
    return (
      <Grid item xs={12}>
        <BrowseInSearchCollections eventingData={eventingData} skill={skill} />
        <PartnerSection skillName={skill.skillName} pageConfigComponentTrackingData={pageConfigComponentTrackingData} />
        <RelatedTopics skill={skill} />
        <Divider css={styles.dividerStyles} />
      </Grid>
    );
  } else {
    return null;
  }
};

export default BrowseInSearch;
